import { http } from './http'
import type {
  IBackendCall,
  TTransferMode,
} from 'src/store/modules/twilio/state'
import { Call, Device, TwilioError } from '@twilio/voice-sdk'
import store from 'src/store'
import * as communication from 'src/store/modules/communication'
import { AxiosResponse } from 'axios'

export interface IGetCallHistoryParams {
  perPage?: number
  cursor?: string
  search?: string
  subscriberStorageUniqId?: string
  assignedUserId?: number
  accountId?: number
  accountDriver?: string
  filterByCreatedAt?: string[]
}

export const getCallHistory = ({
  perPage = 50,
  ...params
}: IGetCallHistoryParams) =>
  http.get('/calls', {
    params: { ...params, perPage },
  })

/** Backend call resolved by a Twilio CallSid. The interceptor already unwraps `data.data` */
export type IResolvedCall = IBackendCall

/** Finds the backend call of a Twilio call. `callSid` is sent as `call_sid` */
export const resolveCall = (callSid: string): Promise<IResolvedCall> =>
  http
    .get<IResolvedCall>('/calls/resolve', { params: { callSid } })
    .then((response) => response.data)

/** Puts the client on hold. The backend escalates a hybrid call to a conference itself */
export const holdCall = (callId: number): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/hold`)
    .then((response) => response.data)

/** Takes the client off hold. Only a conference call that is already on hold */
export const unholdCall = (callId: number): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/unhold`)
    .then((response) => response.data)

/** Adds a user to the call. `userId` is sent as `user_id`. A hybrid call is escalated by the backend */
export const addCallParticipant = (
  callId: number,
  userId: number
): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/participants`, { userId })
    .then((response) => response.data)

/**
 * Starts a blind or warm transfer to a workspace user.
 * `userId` is sent as `user_id`. A hybrid call is moved to a conference by the backend.
 */
export const transferCall = (
  callId: number,
  userId: number,
  mode: TTransferMode
): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/transfer`, { userId, mode })
    .then((response) => response.data)

/** Finishes a warm transfer that is already in `consulting` */
export const completeTransfer = (callId: number): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/transfer/complete`)
    .then((response) => response.data)

/** Stops a transfer while the target is ringing or during a warm consult */
export const cancelTransfer = (callId: number): Promise<IResolvedCall> =>
  http
    .post<IResolvedCall>(`/calls/${callId}/transfer/cancel`)
    .then((response) => response.data)

/** Current backend call. Used when complete/cancel disagree with the local transfer state */
export const getCall = (callId: number): Promise<IResolvedCall> =>
  http.get<IResolvedCall>(`/calls/${callId}`).then((response) => response.data)

export const getCallRecordingUrl = (callId: string): Promise<string> => {
  return http
    .get<string>(`/calls/${callId}/recording/get-url`, {
      // The player shows its own "no access" state
      skipForbiddenToast: true,
    })
    .then((response: AxiosResponse<string>) => {
      return response.data
    })
}

export async function startupClient() {
  try {
    const dataToken = await getTwilioToken()

    return intitializeDevice(dataToken.token)
  } catch (err) {
    store.dispatch(communication.actions.setTwillioState(false))
  }
}

function intitializeDevice(token: string) {
  const device = new Device(token)

  device.register()

  return device
}

interface ITwilioTokenResponse {
  token: string
}

export const getTwilioToken = (): Promise<ITwilioTokenResponse> => {
  return http
    .get<ITwilioTokenResponse>('/twilio/token')
    .then((response: AxiosResponse<ITwilioTokenResponse>) => {
      return response.data
    })
}

/** Storage object bound to the number of a call (as it comes from the backend, snake_case) */
export interface ICallSubscriberObject {
  primary?: string
  secondary?: string
  icon?: string
  uniq_id?: string
  object_scheme_identifier?: string
}

/**
 * Subscriber data the backend puts into the call custom parameters.
 * Returns the first storage object, or null if there is no data / it is empty / broken.
 */
export const getCallSubscriberObject = (
  call: Call
): ICallSubscriberObject | null => {
  const raw = call.customParameters?.get('CusmanSubscriberJson')

  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    const objects = parsed?.storage_objects

    return Array.isArray(objects) && objects[0] ? objects[0] : null
  } catch {
    return null
  }
}

/** Event of a single call, forwarded by the manager with the call it belongs to */
export type TwilioCallEvent =
  | { type: 'ringing'; call: Call; hasEarlyMedia: boolean }
  | { type: 'accept'; call: Call }
  | { type: 'disconnect'; call: Call }
  | { type: 'cancel'; call: Call }
  | { type: 'reject'; call: Call }
  | { type: 'error'; call: Call; error: TwilioError.TwilioError }
  | { type: 'mute'; call: Call; isMuted: boolean }
  | { type: 'reconnecting'; call: Call; error: TwilioError.TwilioError }
  | { type: 'reconnected'; call: Call }
  | { type: 'warning'; call: Call; name: string; data: unknown }
  | { type: 'warning-cleared'; call: Call; name: string }
  // High-frequency events, don't log them blindly
  | { type: 'volume'; call: Call; inputVolume: number; outputVolume: number }
  | { type: 'sample'; call: Call; sample: unknown }

export type TwilioDeviceEvents = {
  registered: () => void
  unregistered: () => void
  registering: () => void
  error: (error: TwilioError.TwilioError, call?: Call) => void
  incoming: (call: Call) => void
  tokenWillExpire: () => void
  /** Active call changed (outgoing/incoming accepted, ended, etc.). null — no active call */
  callChanged: (call: Call | null) => void
  /** Call ended in any way: disconnect / cancel / reject / error */
  callEnded: (call: Call) => void
  /** Any event of any call (ringing, accept, mute, disconnect, ...) */
  callEvent: (event: TwilioCallEvent) => void
}

type EventName = keyof TwilioDeviceEvents

// Events after which the call is considered finished
const CALL_END_EVENTS = ['disconnect', 'cancel', 'reject', 'error'] as const

export class TwilioDeviceManager {
  private device: Device | null = null
  private activeCall: Call | null = null
  private listeners: { [K in EventName]?: Set<TwilioDeviceEvents[K]> } = {}

  /** Device lifecycle */

  /**
   * Creates the device and binds its events. Does NOT register it:
   * subscribe to events first, then call `register()`.
   */
  initialize(token: string, options: Device.Options = {}) {
    // Re-initialization must not leave the old device alive
    this.destroy()

    const device = new Device(token, options)
    this.device = device
    this.bindDeviceEvents(device)

    return device
  }

  destroy() {
    if (this.device) {
      this.device.removeAllListeners()
      this.device.disconnectAll()
      this.device.destroy()
    }

    this.device = null
    this.setActiveCall(null)
  }

  getDevice() {
    return this.device
  }

  isInitialized() {
    return this.device !== null
  }

  isRegistered() {
    return this.device?.state === Device.State.Registered
  }

  /** Resolves when the device is actually registered, rejects if it becomes unregistered */
  async register() {
    await this.requireDevice().register()
  }

  async unregister() {
    await this.device?.unregister()
  }

  updateToken(token: string) {
    this.device?.updateToken(token)
  }

  /** Subscriptions. Returns an unsubscribe function */

  on<K extends EventName>(event: K, handler: TwilioDeviceEvents[K]) {
    let set = this.listeners[event] as Set<TwilioDeviceEvents[K]> | undefined

    if (!set) {
      set = new Set()
      this.listeners[event] = set as never
    }

    set.add(handler)

    return () => this.off(event, handler)
  }

  off<K extends EventName>(event: K, handler: TwilioDeviceEvents[K]) {
    ;(this.listeners[event] as Set<TwilioDeviceEvents[K]> | undefined)?.delete(
      handler
    )
  }

  /** Calls */

  getActiveCall() {
    return this.activeCall
  }

  hasActiveCall() {
    return this.activeCall !== null
  }

  async connect(params: Record<string, string | number>) {
    const device = this.requireDevice()

    const call = await device.connect({
      params: params as Record<string, string>,
    })

    this.attachCall(call)

    return call
  }

  /** Accept the incoming call (the one that came via the `incoming` event or the current one) */
  accept(call: Call | null = this.activeCall) {
    call?.accept()
  }

  reject(call: Call | null = this.activeCall) {
    call?.reject()
  }

  disconnect(call: Call | null = this.activeCall) {
    call?.disconnect()
  }

  disconnectAll() {
    this.device?.disconnectAll()
  }

  sendDigits(digits: string | number, call: Call | null = this.activeCall) {
    call?.sendDigits(String(digits))
  }

  /** Toggles mute. Returns the new state, or null if there is no call */
  toggleMute(call: Call | null = this.activeCall) {
    if (!call) return null

    const next = !call.isMuted()
    call.mute(next)

    return next
  }

  setMuted(muted: boolean, call: Call | null = this.activeCall) {
    call?.mute(muted)
  }

  isMuted(call: Call | null = this.activeCall) {
    return call?.isMuted() ?? false
  }

  /** Audio devices */

  getInputDevices() {
    return Array.from(this.device?.audio?.availableInputDevices.values() ?? [])
  }

  getOutputDevices() {
    return Array.from(this.device?.audio?.availableOutputDevices.values() ?? [])
  }

  async setInputDevice(deviceId: string) {
    await this.device?.audio?.setInputDevice(deviceId)
  }

  async setOutputDevice(deviceId: string) {
    await this.device?.audio?.speakerDevices.set(deviceId)
  }

  /** Internals */

  /**
   * Subscribes the call to its end events and makes it the active one.
   * Used for outgoing calls and for incoming calls from the `incoming` event.
   */
  private attachCall(call: Call) {
    this.setActiveCall(call)
    this.forwardCallEvents(call)

    let ended = false

    const handleEnd = () => {
      if (ended) return
      ended = true

      this.emit('callEnded', call)

      // A newer call may have already replaced this one
      if (this.activeCall === call) this.setActiveCall(null)
    }

    CALL_END_EVENTS.forEach((event) => call.on(event, handleEnd))
  }

  /** Re-emits the call's own events as `callEvent`. Registered before the end handler on purpose. */
  private forwardCallEvents(call: Call) {
    const forward = (event: TwilioCallEvent) => this.emit('callEvent', event)

    call.on('ringing', (hasEarlyMedia: boolean) =>
      forward({ type: 'ringing', call, hasEarlyMedia })
    )
    call.on('accept', () => forward({ type: 'accept', call }))
    call.on('disconnect', () => forward({ type: 'disconnect', call }))
    call.on('cancel', () => forward({ type: 'cancel', call }))
    call.on('reject', () => forward({ type: 'reject', call }))
    call.on('error', (error: TwilioError.TwilioError) =>
      forward({ type: 'error', call, error })
    )
    call.on('mute', (isMuted: boolean) =>
      forward({ type: 'mute', call, isMuted })
    )
    call.on('reconnecting', (error: TwilioError.TwilioError) =>
      forward({ type: 'reconnecting', call, error })
    )
    call.on('reconnected', () => forward({ type: 'reconnected', call }))
    call.on('warning', (name: string, data: unknown) =>
      forward({ type: 'warning', call, name, data })
    )
    call.on('warning-cleared', (name: string) =>
      forward({ type: 'warning-cleared', call, name })
    )
    call.on('volume', (inputVolume: number, outputVolume: number) =>
      forward({ type: 'volume', call, inputVolume, outputVolume })
    )
    call.on('sample', (sample: unknown) =>
      forward({ type: 'sample', call, sample })
    )
  }

  private setActiveCall(call: Call | null) {
    if (this.activeCall === call) return

    this.activeCall = call
    this.emit('callChanged', call)
  }

  private bindDeviceEvents(device: Device) {
    device.on('registering', () => this.emit('registering'))
    device.on('registered', () => this.emit('registered'))
    device.on('unregistered', () => this.emit('unregistered'))
    device.on('error', (error, call) => this.emit('error', error, call))

    device.on('incoming', (call: Call) => {
      this.attachCall(call)
      this.emit('incoming', call)
    })

    device.on('tokenWillExpire', async () => {
      this.emit('tokenWillExpire')

      // // Refresh the token ourselves, otherwise the device gets unregistered on expiration
      // try {
      //   const { token } = await getTwilioToken()
      //   this.updateToken(token)
      // } catch (err) {
      //   this.emit('error', err as TwilioError.TwilioError)
      // }
    })
  }

  private emit<K extends EventName>(
    event: K,
    ...args: Parameters<TwilioDeviceEvents[K]>
  ) {
    const set = this.listeners[event] as Set<TwilioDeviceEvents[K]> | undefined

    set?.forEach((handler) => {
      try {
        ;(handler as (...a: unknown[]) => void)(...args)
      } catch (err) {
        console.error(`[TwilioDeviceManager] "${event}" handler failed`, err)
      }
    })
  }

  private requireDevice() {
    if (!this.device) {
      throw new Error('TwilioDeviceManager: device is not initialized')
    }

    return this.device
  }
}

// Single manager instance for the whole app
export const twilioDeviceManager = new TwilioDeviceManager()
